import { API_ENDPOINTS } from '@/config/api';
import { useAuthStore } from '@/stores/auth';
import { handleResponse } from '@/utils/auth';
import type { UserProfileResponse } from '@/types';

class UserService {
  /**
   * Get access token from auth store and determine auth type
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const authStore = useAuthStore();
    const token = await authStore.getAccessToken();
    
    // Check if we're using session-based authentication
    const authType = localStorage.getItem('joblogic-gpt-auth-type');
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
    
    // Only add Authorization header for JWT tokens, not session tokens
    if (token && authType !== 'session' && !token.startsWith('session-')) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }

  /**
   * Fetch current user profile information
   */
  async getUserProfile(): Promise<UserProfileResponse> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(API_ENDPOINTS.auth.me, {
        method: 'GET',
        credentials: 'include', // Include cookies for session-based auth
        headers,
      });
      await handleResponse(response);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch user profile' }));
        throw new Error(errorData.detail || `Request failed with status: ${response.status}`);
      }

      const data: UserProfileResponse = await response.json();
      return data;
    } catch (error) {
      console.error('Get user profile failed:', error);
      throw error;
    }
  }
}

export const userService = new UserService();
